import React, { useState, useEffect, useRef, useContext } from 'react'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { InputText } from 'primereact/inputtext'
import { Button } from 'primereact/button'
import { Toolbar } from 'primereact/toolbar'
import { Dialog } from 'primereact/dialog'
import { InputTextarea } from 'primereact/inputtextarea'
import { Dropdown } from 'primereact/dropdown'
import { Toast } from 'primereact/toast'
import { Input, InputGroup, InputGroupText } from 'reactstrap'
import server, { API_URL } from '../../server'
import MainContentLayout from '../Layout/MainContentLayout'
import { ToastPopUp } from '../shared/utils'
import { AuthenticationContext } from '../../Auth/authentication.context'
import useTableRowsPerPage from '../../hooks/useTableRowsPerPage'
import { getInitialPaginationState } from '../../shared/tablePagination'

const PROVINCES = [
  { label: 'Alberta', value: 'AB' },
  { label: 'British Columbia', value: 'BC' },
  { label: 'Manitoba', value: 'MB' },
  { label: 'New Brunswick', value: 'NB' },
  { label: 'Newfoundland and Labrador', value: 'NL' },
  { label: 'Northwest Territories', value: 'NT' },
  { label: 'Nova Scotia', value: 'NS' },
  { label: 'Nunavut', value: 'NU' },
  { label: 'Ontario', value: 'ON' },
  { label: 'Prince Edward Island', value: 'PE' },
  { label: 'Quebec', value: 'QC' },
  { label: 'Saskatchewan', value: 'SK' },
  { label: 'Yukon', value: 'YT' },
]

export default function PharmacyGroupsTable() {
  const { user } = useContext(AuthenticationContext)
  const { defaultRows, rowsPerPageOptions, applyPageEvent } = useTableRowsPerPage()
  const [mainPageState, setMainPageState] = useState(() =>
    getInitialPaginationState(user)
  )
  const [dialogPageState, setDialogPageState] = useState(() =>
    getInitialPaginationState(user)
  )
  const [pharmacyGroups, setPharmacyGroups] = useState([])
  const [selectedGroup, setSelectedGroup] = useState(null)
  const [tableLoading, setTableLoading] = useState(false)
  const [groupDialog, setGroupDialog] = useState(false)
  const [deleteDialog, setDeleteDialog] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [groupPharmacies, setGroupPharmacies] = useState([])
  const [pharmaciesLoading, setPharmaciesLoading] = useState(false)
  const [adminUsers, setAdminUsers] = useState([])
  const toast = useRef(null)

  const emptyGroup = {
    name: '',
    province: '',
    groupOwnerId: null,
    logo: '',
    favicon: '',
    loginBackground: '',
    domain: '',
    primaryColor: '',
    secondaryColor: '',
    accessCode: '',
  }

  const [group, setGroup] = useState(emptyGroup)
  const [accessCodeTouched, setAccessCodeTouched] = useState(false)
  const [showAccessCodeInForm, setShowAccessCodeInForm] = useState(false)
  const [revealedAccessCodeIds, setRevealedAccessCodeIds] = useState(new Set())
  const [logoUploading, setLogoUploading] = useState(false)
  const [faviconUploading, setFaviconUploading] = useState(false)
  const [loginBackgroundUploading, setLoginBackgroundUploading] = useState(false)
  const logoInputRef = useRef(null)
  const faviconInputRef = useRef(null)
  const loginBackgroundInputRef = useRef(null)

  useEffect(() => {
    setMainPageState((prev) => ({ ...prev, rows: defaultRows }))
    setDialogPageState((prev) => ({ ...prev, rows: defaultRows }))
  }, [defaultRows])

  useEffect(() => {
    fetchPharmacyGroups()
    fetchAdminUsers()
  }, [])

  const fetchPharmacyGroups = async () => {
    setTableLoading(true)
    try {
      const response = await server.get('/pharmacyGroups')
      setPharmacyGroups(response.data.groups || response.data.pharmacyGroups || [])
    } catch (error) {
      ToastPopUp(toast, 'error', 'Error', 'Failed to fetch pharmacy groups')
    } finally {
      setTableLoading(false)
    }
  }

  const fetchAdminUsers = async () => {
    try {
      const response = await server.get('/users/get-users?role=admin')
      setAdminUsers(
        response.data.users?.map((user) => ({
          label: user.displayName || user.email,
          value: user.id,
        })) || []
      )
    } catch (error) {
      console.error('Error fetching admin users:', error)
    }
  }

  const fetchGroupPharmacies = async (groupId) => {
    setPharmaciesLoading(true)
    try {
      const response = await server.get(`/pharmacyGroups/${groupId}`)
      setGroupPharmacies(response.data.pharmacyGroup?.pharmacies || [])
    } catch (error) {
      ToastPopUp(toast, 'error', 'Error', 'Failed to fetch pharmacies')
      setGroupPharmacies([])
    } finally {
      setPharmaciesLoading(false)
    }
  }

  const openNew = () => {
    setGroup(emptyGroup)
    setAccessCodeTouched(false)
    setShowAccessCodeInForm(false)
    setSubmitted(false)
    setGroupDialog(true)
    setGroupPharmacies([])
  }

  const openEdit = (groupData) => {
    setGroup({
      ...groupData,
      groupOwnerId: groupData.groupOwnerId || groupData.groupOwner?.id || null,
      favicon: groupData.favicon || '',
      loginBackground: groupData.loginBackground || '',
      accessCode: '', // Never pre-fill for security; admin enters new value to change
    })
    setAccessCodeTouched(false)
    setShowAccessCodeInForm(false)
    setSubmitted(false)
    setGroupDialog(true)
    if (groupData.id) {
      fetchGroupPharmacies(groupData.id)
    }
  }

  const hideDialog = () => {
    setSubmitted(false)
    setAccessCodeTouched(false)
    setShowAccessCodeInForm(false)
    setGroupDialog(false)
    setGroup(emptyGroup)
    setGroupPharmacies([])
  }

  const hideDeleteDialog = () => {
    setDeleteDialog(false)
    setSelectedGroup(null)
  }

  const saveGroup = async () => {
    setSubmitted(true)

    if (!group.name || !group.province || !group.groupOwnerId) {
      ToastPopUp(toast, 'warn', 'Validation', 'Please fill in all required fields')
      return
    }

    try {
      const groupData = {
        name: group.name,
        province: group.province,
        groupOwnerId: group.groupOwnerId,
        logo: group.logo || null,
        favicon: group.favicon || null,
        loginBackground: group.loginBackground || null,
        domain: group.domain || null,
        primaryColor: group.primaryColor || null,
        secondaryColor: group.secondaryColor || null,
      }

      if (group.id) {
        if (accessCodeTouched) {
          groupData.accessCode = group.accessCode?.trim() || null
        }
      } else {
        groupData.accessCode = group.accessCode?.trim() || null
      }

      if (group.id) {
        // Update existing group
        await server.patch(`/pharmacyGroups/${group.id}`, groupData)
        ToastPopUp(toast, 'success', 'Success', 'Pharmacy group updated successfully')
      } else {
        // Create new group
        await server.post('/pharmacyGroups', groupData)
        ToastPopUp(toast, 'success', 'Success', 'Pharmacy group created successfully')
      }

      hideDialog()
      fetchPharmacyGroups()
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || 'Failed to save pharmacy group'
      ToastPopUp(toast, 'error', 'Error', errorMessage)
    }
  }

  const handleLogoUpload = async (e) => {
    const file = e?.target?.files?.[0]
    if (!file) return
    const allowed = /\.(png|jpe?g|gif|webp|svg)$/i
    if (!allowed.test(file.name)) {
      ToastPopUp(toast, 'error', 'Invalid file', 'Only PNG, JPG, GIF, WebP, or SVG images are allowed')
      return
    }
    setLogoUploading(true)
    try {
      const formData = new FormData()
      formData.append('logo', file)
      const response = await server.post('/pharmacyGroups/upload-logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      const logoPath = response.data?.logo
      if (logoPath) {
        setGroup({ ...group, logo: logoPath })
      }
    } catch (err) {
      ToastPopUp(toast, 'error', 'Upload failed', err.response?.data?.message || 'Failed to upload logo')
    } finally {
      setLogoUploading(false)
      if (logoInputRef.current) logoInputRef.current.value = ''
    }
  }

  const handleFaviconUpload = async (e) => {
    const file = e?.target?.files?.[0]
    if (!file) return
    const allowed = /\.(png|jpe?g|gif|webp|svg|ico)$/i
    if (!allowed.test(file.name)) {
      ToastPopUp(toast, 'error', 'Invalid file', 'Only PNG, JPG, GIF, WebP, SVG, or ICO images are allowed')
      return
    }
    setFaviconUploading(true)
    try {
      const formData = new FormData()
      formData.append('favicon', file)
      const response = await server.post('/pharmacyGroups/upload-favicon', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      const faviconPath = response.data?.favicon
      if (faviconPath) {
        setGroup({ ...group, favicon: faviconPath })
      }
    } catch (err) {
      ToastPopUp(toast, 'error', 'Upload failed', err.response?.data?.message || 'Failed to upload favicon')
    } finally {
      setFaviconUploading(false)
      if (faviconInputRef.current) faviconInputRef.current.value = ''
    }
  }
  const handleLoginBackgroundUpload = async (e) => {
    const file = e?.target?.files?.[0]
    if (!file) return
    const allowed = /\.(png|jpe?g|gif|webp)$/i
    if (!allowed.test(file.name)) {
      ToastPopUp(toast, 'error', 'Invalid file', 'Only PNG, JPG, GIF, or WebP images are allowed')
      return
    }
    // Matches the 5MB limit on /pharmacyGroups/upload-login-background
    if (file.size > 5 * 1024 * 1024) {
      ToastPopUp(toast, 'error', 'File too large', 'Login background must be 5MB or smaller')
      return
    }
    setLoginBackgroundUploading(true)
    try {
      const formData = new FormData()
      formData.append('loginBackground', file)
      const response = await server.post('/pharmacyGroups/upload-login-background', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      const loginBackgroundPath = response.data?.loginBackground
      if (loginBackgroundPath) {
        setGroup({ ...group, loginBackground: loginBackgroundPath })
      }
    } catch (err) {
      ToastPopUp(toast, 'error', 'Upload failed', err.response?.data?.message || 'Failed to upload login background')
    } finally {
      setLoginBackgroundUploading(false)
      if (loginBackgroundInputRef.current) loginBackgroundInputRef.current.value = ''
    }
  }

  const confirmDelete = () => {
    setDeleteDialog(true)
  }

  const deleteGroup = async () => {
    try {
      await server.delete(`/pharmacyGroups/${selectedGroup.id}`)
      ToastPopUp(toast, 'success', 'Success', 'Pharmacy group deleted successfully')
      hideDeleteDialog()
      fetchPharmacyGroups()
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || 'Failed to delete pharmacy group'
      ToastPopUp(toast, 'error', 'Error', errorMessage)
    }
  }

  const leftToolbarTemplate = () => {
    return (
      <div className='d-flex gap-2'>
        <Button
          label='New'
          icon='pi pi-plus'
          className='p-button-success'
          onClick={openNew}
        />
      </div>
    )
  }

  const actionBodyTemplate = (rowData) => {
    return (
      <div className='d-flex justify-content-center gap-2'>
        <Button
          icon='pi pi-pencil'
          rounded
          outlined
          onClick={() => openEdit(rowData)}
        />
        <Button
          icon='pi pi-trash'
          rounded
          outlined
          severity='danger'
          onClick={() => {
            setSelectedGroup(rowData)
            confirmDelete()
          }}
        />
      </div>
    )
  }

  const provinceBodyTemplate = (rowData) => {
    const province = PROVINCES.find((p) => p.value === rowData.province)
    return province ? province.label : rowData.province
  }

  const toggleRevealAccessCode = (groupId) => {
    setRevealedAccessCodeIds((prev) => {
      const next = new Set(prev)
      if (next.has(groupId)) {
        next.delete(groupId)
      } else {
        next.add(groupId)
      }
      return next
    })
  }

  const accessCodeBodyTemplate = (rowData) => {
    if (!rowData.accessCode) {
      return <span className='text-muted'>—</span>
    }
    const isRevealed = revealedAccessCodeIds.has(rowData.id)
    return (
      <div className='d-flex align-items-center gap-2'>
        <span>{isRevealed ? rowData.accessCode : '••••••'}</span>
        <Button
          icon={isRevealed ? 'pi pi-eye-slash' : 'pi pi-eye'}
          rounded
          text
          size='small'
          className='p-button-sm'
          onClick={() => toggleRevealAccessCode(rowData.id)}
          aria-label={isRevealed ? 'Hide access code' : 'Show access code'}
        />
      </div>
    )
  }

  const groupDialogFooter = (
    <div className='d-flex gap-2'>
      <Button
        label='Cancel'
        icon='pi pi-times'
        className='p-button-text'
        onClick={hideDialog}
      />
      <Button label='Save' icon='pi pi-check' onClick={saveGroup} />
    </div>
  )

  const deleteDialogFooter = (
    <div className='d-flex gap-2'>
      <Button
        label='No'
        icon='pi pi-times'
        className='p-button-text'
        onClick={hideDeleteDialog}
      />
      <Button
        label='Yes'
        icon='pi pi-check'
        className='p-button-danger'
        onClick={deleteGroup}
      />
    </div>
  )

  return (
    <MainContentLayout title='Pharmacy Groups'>
      <Toast ref={toast} />
      <div className='card'>
        <Toolbar className='mb-4' left={leftToolbarTemplate}></Toolbar>

        <DataTable
          value={pharmacyGroups}
          loading={tableLoading}
          paginator
          rows={mainPageState.rows}
          first={mainPageState.first}
          onPage={(e) =>
            setMainPageState((prev) => applyPageEvent(prev, e))
          }
          rowsPerPageOptions={rowsPerPageOptions}
          emptyMessage='No pharmacy groups found.'
        >
          <Column field='name' header='Name' sortable />
          <Column
            field='province'
            header='Province'
            body={provinceBodyTemplate}
            sortable
          />
          <Column
            field='groupOwner.displayName'
            header='Group Owner'
            sortable
          />
          <Column field='domain' header='Domain' />
          <Column
            field='accessCode'
            header='Access Code'
            body={accessCodeBodyTemplate}
          />
          <Column
            body={actionBodyTemplate}
            header='Actions'
            exportable={false}
            style={{ minWidth: '8rem' }}
          />
        </DataTable>
      </div>

      <Dialog
        visible={groupDialog}
        style={{ width: '800px' }}
        header={group.id ? 'Edit Pharmacy Group' : 'New Pharmacy Group'}
        modal
        className='p-fluid'
        footer={groupDialogFooter}
        onHide={hideDialog}
      >
        <div className='row'>
          <div className='col-md-6 mb-3'>
            <label htmlFor='name' className='form-label'>
              Name <span className='text-danger'>*</span>
            </label>
            <InputText
              id='name'
              value={group.name}
              onChange={(e) => setGroup({ ...group, name: e.target.value })}
              required
              autoFocus
              className={submitted && !group.name ? 'p-invalid' : ''}
            />
            {submitted && !group.name && (
              <small className='p-error'>Name is required.</small>
            )}
          </div>

          <div className='col-md-6 mb-3'>
            <label htmlFor='province' className='form-label'>
              Province <span className='text-danger'>*</span>
            </label>
            <Dropdown
              id='province'
              value={group.province}
              options={PROVINCES}
              onChange={(e) => setGroup({ ...group, province: e.value })}
              placeholder='Select Province'
              className={submitted && !group.province ? 'p-invalid' : ''}
            />
            {submitted && !group.province && (
              <small className='p-error'>Province is required.</small>
            )}
          </div>

          <div className='col-md-6 mb-3'>
            <label htmlFor='groupOwnerId' className='form-label'>
              Group Owner <span className='text-danger'>*</span>
            </label>
            <Dropdown
              id='groupOwnerId'
              value={group.groupOwnerId}
              options={adminUsers}
              onChange={(e) => setGroup({ ...group, groupOwnerId: e.value })}
              placeholder='Select Group Owner'
              className={submitted && !group.groupOwnerId ? 'p-invalid' : ''}
            />
            {submitted && !group.groupOwnerId && (
              <small className='p-error'>Group Owner is required.</small>
            )}
          </div>

          <div className='col-md-6 mb-3'>
            <label htmlFor='domain' className='form-label'>
              Domain
            </label>
            <InputText
              id='domain'
              value={group.domain}
              onChange={(e) => setGroup({ ...group, domain: e.target.value })}
              placeholder='example.com'
            />
          </div>

          <div className='col-md-6 mb-3'>
            <label htmlFor='accessCode' className='form-label'>
              Access Code
            </label>
            <InputGroup>
              <Input
                id='accessCode'
                type={showAccessCodeInForm ? 'text' : 'password'}
                value={group.accessCode}
                onChange={(e) => {
                  setAccessCodeTouched(true)
                  setGroup({ ...group, accessCode: e.target.value })
                }}
                placeholder={group.id ? 'Enter new code (leave blank to keep current)' : 'Required for branded portal login'}
                autoComplete='off'
                className='form-control'
              />
              <InputGroupText
                onClick={() => setShowAccessCodeInForm((prev) => !prev)}
                style={{ cursor: 'pointer' }}
                title={showAccessCodeInForm ? 'Hide access code' : 'Show access code'}
              >
                <i className={`mdi ${showAccessCodeInForm ? 'mdi-eye-off-outline' : 'mdi-eye-outline'}`} />
              </InputGroupText>
            </InputGroup>
            {group.id && (
              <small className='text-muted'>
                Leave blank to keep the current code. Enter a new value to change, or clear and save to remove.
              </small>
            )}
          </div>

          <div className='col-md-6 mb-3'>
            <label htmlFor='logo' className='form-label'>
              Logo
            </label>
            <div className='d-flex flex-column gap-2'>
              <input
                ref={logoInputRef}
                type='file'
                id='logo'
                accept='.png,.jpg,.jpeg,.gif,.webp,.svg'
                onChange={handleLogoUpload}
                disabled={logoUploading}
                className='form-control'
              />
              {group.logo && (
                <div className='d-flex align-items-center gap-2'>
                  <img
                    src={
                      group.logo.startsWith('http')
                        ? group.logo
                        : `${(API_URL || '').replace(/\/api\/?$/, '')}${group.logo.startsWith('/') ? '' : '/'}${group.logo}`
                    }
                    alt='Group logo'
                    style={{ maxHeight: 60, maxWidth: 120, objectFit: 'contain' }}
                  />
                  <Button
                    type='button'
                    icon='pi pi-times'
                    rounded
                    outlined
                    severity='secondary'
                    size='small'
                    onClick={() => setGroup({ ...group, logo: '' })}
                    title='Remove logo'
                  />
                </div>
              )}
              {logoUploading && <small className='text-muted'>Uploading...</small>}
            </div>
          </div>

          <div className='col-md-6 mb-3'>
            <label htmlFor='favicon' className='form-label'>
              Favicon
            </label>
            <small className='d-block text-muted mb-1'>
              Used for browser tab and collapsed sidebar. Square images (e.g. 32×32) work best.
            </small>
            <div className='d-flex flex-column gap-2'>
              <input
                ref={faviconInputRef}
                type='file'
                id='favicon'
                accept='.png,.jpg,.jpeg,.gif,.webp,.svg,.ico'
                onChange={handleFaviconUpload}
                disabled={faviconUploading}
                className='form-control'
              />
              {group.favicon && (
                <div className='d-flex align-items-center gap-2'>
                  <img
                    src={
                      group.favicon.startsWith('http')
                        ? group.favicon
                        : `${(API_URL || '').replace(/\/api\/?$/, '')}${group.favicon.startsWith('/') ? '' : '/'}${group.favicon}`
                    }
                    alt='Group favicon'
                    style={{ maxHeight: 32, maxWidth: 32, objectFit: 'contain' }}
                  />
                  <Button
                    type='button'
                    icon='pi pi-times'
                    rounded
                    outlined
                    severity='secondary'
                    size='small'
                    onClick={() => setGroup({ ...group, favicon: '' })}
                    title='Remove favicon'
                  />
                </div>
              )}
              {faviconUploading && <small className='text-muted'>Uploading...</small>}
            </div>
          </div>

          <div className='col-md-6 mb-3'>
            <label htmlFor='loginBackground' className='form-label'>
              Login Background
            </label>
            <small className='d-block text-muted mb-1'>
              Shown behind the logo on the group login screens. Large landscape or portrait photos (e.g. 1600×1200) work best.
            </small>
            <div className='d-flex flex-column gap-2'>
              <input
                ref={loginBackgroundInputRef}
                type='file'
                id='loginBackground'
                accept='.png,.jpg,.jpeg,.gif,.webp'
                onChange={handleLoginBackgroundUpload}
                disabled={loginBackgroundUploading}
                className='form-control'
              />
              {group.loginBackground && (
                <div className='d-flex align-items-center gap-2'>
                  <img
                    src={
                      group.loginBackground.startsWith('http')
                        ? group.loginBackground
                        : `${(API_URL || '').replace(/\/api\/?$/, '')}${group.loginBackground.startsWith('/') ? '' : '/'}${group.loginBackground}`
                    }
                    alt='Group login background'
                    style={{ maxHeight: 80, maxWidth: 140, objectFit: 'cover', borderRadius: 4 }}
                  />
                  <Button
                    type='button'
                    icon='pi pi-times'
                    rounded
                    outlined
                    severity='secondary'
                    size='small'
                    onClick={() => setGroup({ ...group, loginBackground: '' })}
                    title='Remove login background'
                  />
                </div>
              )}
              {loginBackgroundUploading && <small className='text-muted'>Uploading...</small>}
            </div>
          </div>

          <div className='col-md-3 mb-3'>
            <label htmlFor='primaryColor' className='form-label'>
              Sidebar Color
            </label>
            <div className='d-flex gap-2 align-items-center'>
              <input
                type='color'
                value={group.primaryColor || '#626ED4'}
                onChange={(e) =>
                  setGroup({ ...group, primaryColor: e.target.value })
                }
                style={{ width: '50px', height: '38px', border: '1px solid #ced4da', borderRadius: '4px' }}
              />
              <InputText
                id='primaryColor'
                value={group.primaryColor || ''}
                onChange={(e) =>
                  setGroup({ ...group, primaryColor: e.target.value })
                }
                placeholder='#626ED4'
                maxLength={7}
              />
            </div>
          </div>

          <div className='col-md-3 mb-3'>
            <label htmlFor='secondaryColor' className='form-label'>
              Buttons Color
            </label>
            <div className='d-flex gap-2 align-items-center'>
              <input
                type='color'
                value={group.secondaryColor || group.primaryColor || '#626ED4'}
                onChange={(e) =>
                  setGroup({ ...group, secondaryColor: e.target.value })
                }
                style={{ width: '50px', height: '38px', border: '1px solid #ced4da', borderRadius: '4px' }}
              />
              <InputText
                id='secondaryColor'
                value={group.secondaryColor || ''}
                onChange={(e) =>
                  setGroup({ ...group, secondaryColor: e.target.value })
                }
                placeholder={group.primaryColor || '#626ED4'}
                maxLength={7}
              />
            </div>
            <small className='d-block text-muted mt-1'>
              Accent for buttons, tables and highlights. Defaults to the sidebar color.
            </small>
          </div>
        </div>

        {group.id && (
          <div className='mt-4'>
            <h5>Pharmacies in this Group</h5>
            <DataTable
              value={groupPharmacies}
              loading={pharmaciesLoading}
              emptyMessage='No pharmacies in this group.'
              paginator
              rows={dialogPageState.rows}
              first={dialogPageState.first}
              onPage={(e) =>
                setDialogPageState((prev) => applyPageEvent(prev, e))
              }
              rowsPerPageOptions={rowsPerPageOptions}
            >
              <Column field='name' header='Name' />
              <Column field='legalName' header='Legal Name' />
              <Column field='tradeName' header='Trade Name' />
              <Column
                field='user.email'
                header='Email'
                body={(rowData) => rowData.user?.email || 'N/A'}
              />
            </DataTable>
          </div>
        )}
      </Dialog>

      <Dialog
        visible={deleteDialog}
        style={{ width: '450px' }}
        header='Confirm'
        modal
        footer={deleteDialogFooter}
        onHide={hideDeleteDialog}
      >
        <div className='confirmation-content'>
          <i
            className='pi pi-exclamation-triangle mr-3'
            style={{ fontSize: '2rem' }}
          />
          {selectedGroup && (
            <span>
              Are you sure you want to delete <b>{selectedGroup.name}</b>?
            </span>
          )}
        </div>
      </Dialog>
    </MainContentLayout>
  )
}

